import React, { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@desygner/ui-common-components';

import { TableOfContentContext } from './TableOfContent.context';
import {
  StyleTocFooterButton,
  StyledTocContainer,
  StyledTocFooter,
  StyledTocInnerFooter,
} from './TableOfContent.style';
import type {
  LeaderOption,
  TableOfContentData,
  TableOfContentFormat,
  TableOfContentLabel,
  TableOfContentLeaderStyle,
  TableOfContentPreview,
} from './TableOfContent.type';
import { TableOfContentBody } from './components/TableOfContentBody';

export interface TableOfContentProps {
  leaderOptions: LeaderOption[];
  label: TableOfContentLabel;
  previewSource: TableOfContentPreview;
  iniLeaderStyle: TableOfContentLeaderStyle;
  tableFormat: TableOfContentFormat;
  levels: number;
  maxContentLevels: number;
  isPageNumber: boolean;
  isAlignRight: boolean;
  isLinkUsed: boolean;
  onCancel: () => void;
  onInsert: (data: TableOfContentData) => void;
}

export const TableOfContent = memo(function TableOfContent({
  onCancel,
  onInsert,
  leaderOptions,
  previewSource,
  label,
  iniLeaderStyle,
  maxContentLevels = 5,
  tableFormat: initTableFormat,
  levels: initLevels,
  isPageNumber: initIsPageNumber,
  isAlignRight: initIsAlignRight,
  isLinkUsed: initIsLinkUsed,
}: TableOfContentProps): React.ReactElement {
  const [tableFormat, setTableFormat] = useState<TableOfContentFormat>(initTableFormat);
  const [leaderStyle, setLeaderStyle] = useState<TableOfContentLeaderStyle>(iniLeaderStyle);
  const [levels, setLevels] = useState<number>(initLevels);
  const [isPageNumber, setIsPageNumber] = useState<boolean>(initIsPageNumber);
  const [isAlignRight, setIsAlignRight] = useState<boolean>(initIsAlignRight);
  const [isLinkUsed, setIsLinkUsed] = useState<boolean>(initIsLinkUsed);

  const handleInsert = useCallback(() => {
    onInsert({
      tableFormat,
      leaderStyle,
      levels,
      isPageNumber,
      isAlignRight,
      isLinkUsed,
    });
  }, [isAlignRight, isLinkUsed, isPageNumber, leaderStyle, levels, onInsert, tableFormat]);

  const contextValue = useMemo(() => {
    return {
      tableFormat,
      setTableFormat,
      leaderStyle,
      setLeaderStyle,
      levels,
      setLevels,
      isPageNumber,
      setIsPageNumber,
      isAlignRight,
      setIsAlignRight,
      isLinkUsed,
      setIsLinkUsed,
    };
  }, [isAlignRight, isLinkUsed, isPageNumber, leaderStyle, levels, tableFormat]);

  return (
    <TableOfContentContext.Provider value={contextValue}>
      <StyledTocContainer>
        <TableOfContentBody
          leaderOptions={leaderOptions}
          label={label}
          previewSource={previewSource}
          maxContentLevels={maxContentLevels}
        />

        <StyledTocFooter>
          <StyledTocInnerFooter>
            <Button onClick={onCancel} variant="ghost">
              <StyleTocFooterButton>{label.cancel}</StyleTocFooterButton>
            </Button>

            <StyleTocFooterButton>
              <Button onClick={handleInsert} variant="primary">
                <StyleTocFooterButton>{label.insert}</StyleTocFooterButton>
              </Button>
            </StyleTocFooterButton>
          </StyledTocInnerFooter>
        </StyledTocFooter>
      </StyledTocContainer>
    </TableOfContentContext.Provider>
  );
});
